import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Sparkles, Loader2, FileText, Lightbulb, Copy } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminAITools() {
  const [tab, setTab] = useState('notes');
  return (
    <AdminLayout title="AI Tools">
      <div className="space-y-5">
        <div className="bg-gradient-to-br from-[#0F172A] to-[#7C3AED] text-white rounded-xl p-6">
          <div className="flex items-center gap-2 mb-1"><Sparkles size={18} /><h1 className="text-2xl font-bold">AI Coaching Tools</h1></div>
          <p className="text-white/85 text-sm">Powered by Claude Sonnet 4.5. All output is brand-locked to business coaching / entrepreneurship.</p>
        </div>
        <div className="flex gap-2 border-b border-[#E2E8F0]">
          <button onClick={() => setTab('notes')} className={`px-3 py-1.5 text-sm font-bold rounded-t ${tab==='notes'?'bg-[#1D4ED8] text-white':'text-[#475569] hover:bg-[#F1F5F9]'}`} data-testid="ai-tab-notes"><FileText size={13} className="inline mr-1.5 -mt-0.5"/>Session Notes</button>
          <button onClick={() => setTab('recs')} className={`px-3 py-1.5 text-sm font-bold rounded-t ${tab==='recs'?'bg-[#1D4ED8] text-white':'text-[#475569] hover:bg-[#F1F5F9]'}`} data-testid="ai-tab-recs"><Lightbulb size={13} className="inline mr-1.5 -mt-0.5"/>Recommendations</button>
        </div>
        {tab === 'notes' ? <SessionNotes/> : <Recommendations/>}
      </div>
    </AdminLayout>
  );
}

function SessionNotes() {
  const [form, setForm] = useState({ client_name:'', session_topic:'', raw_notes:'' });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async () => {
    if (form.raw_notes.length < 20) return toast.error('Raw notes must be at least 20 characters');
    setBusy(true); setResult(null);
    try { const r = await axios.post(`${API}/api/ai/session-notes`, form, { withCredentials:true }); setResult(r.data); toast.success('Notes polished'); }
    catch (e) { toast.error(e.response?.data?.detail || 'AI failed'); } finally { setBusy(false); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3">
        <h3 className="font-bold text-[#0A0F1C]">Input</h3>
        <input className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded text-sm" placeholder="Client name" value={form.client_name} onChange={e=>setForm({...form,client_name:e.target.value})} data-testid="notes-client"/>
        <input className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded text-sm" placeholder="Session topic (optional)" value={form.session_topic} onChange={e=>setForm({...form,session_topic:e.target.value})} data-testid="notes-topic"/>
        <textarea className="w-full px-3 py-2 border border-[#E2E8F0] rounded text-sm font-mono" rows={10} placeholder="Paste rough session notes here..." value={form.raw_notes} onChange={e=>setForm({...form,raw_notes:e.target.value})} data-testid="notes-raw"/>
        <button onClick={submit} disabled={busy} className="btn-primary-orange text-sm w-full" data-testid="notes-submit">{busy?<><Loader2 size={14} className="animate-spin inline mr-1.5"/>Polishing...</>:<>Polish Notes</>}</button>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3 min-h-[320px]" data-testid="notes-result">
        <h3 className="font-bold text-[#0A0F1C]">Polished Output</h3>
        {!result ? <p className="text-sm text-[#94A3B8]">Run polish to see the result.</p> : <>
          <div className="prose prose-sm max-w-none text-[#0A0F1C] whitespace-pre-wrap">{result.polished}</div>
          {result.key_takeaways?.length > 0 && <div><div className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-1">Key Takeaways</div><ul className="list-disc ml-5 text-sm text-[#0A0F1C] space-y-0.5">{result.key_takeaways.map((k,i)=><li key={i}>{k}</li>)}</ul></div>}
          {result.next_steps?.length > 0 && <div><div className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-1">Next Steps</div><ul className="list-disc ml-5 text-sm text-[#0A0F1C] space-y-0.5">{result.next_steps.map((k,i)=><li key={i}>{k}</li>)}</ul></div>}
          <button onClick={()=>{navigator.clipboard.writeText([result.polished,'',...(result.key_takeaways||[]).map(k=>'• '+k),'',...(result.next_steps||[]).map(k=>'→ '+k)].join('\n')); toast.success('Copied')}} className="btn-outline text-xs inline-flex items-center gap-1" data-testid="notes-copy"><Copy size={12}/>Copy all</button>
        </>}
      </div>
    </div>
  );
}

function Recommendations() {
  const [form, setForm] = useState({ client_name:'', context:'' });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async () => {
    if (form.context.length < 30) return toast.error('Context must be at least 30 characters');
    setBusy(true); setResult(null);
    try { const r = await axios.post(`${API}/api/ai/recommendations`, form, { withCredentials:true }); setResult(r.data); toast.success('Recommendations ready'); }
    catch (e) { toast.error(e.response?.data?.detail || 'AI failed'); } finally { setBusy(false); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3">
        <h3 className="font-bold text-[#0A0F1C]">Client Context</h3>
        <input className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded text-sm" placeholder="Client name" value={form.client_name} onChange={e=>setForm({...form,client_name:e.target.value})} data-testid="recs-client"/>
        <textarea className="w-full px-3 py-2 border border-[#E2E8F0] rounded text-sm" rows={10} placeholder="Business stage, goals, blockers, prior sessions, anything relevant..." value={form.context} onChange={e=>setForm({...form,context:e.target.value})} data-testid="recs-context"/>
        <button onClick={submit} disabled={busy} className="btn-primary-orange text-sm w-full" data-testid="recs-submit">{busy?<><Loader2 size={14} className="animate-spin inline mr-1.5"/>Thinking...</>:<>Get Recommendations</>}</button>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3 min-h-[320px]" data-testid="recs-result">
        <h3 className="font-bold text-[#0A0F1C]">Strategic Recommendations</h3>
        {!result ? <p className="text-sm text-[#94A3B8]">Submit context to get strategic focus areas.</p> : <>
          <p className="text-sm text-[#475569] italic">{result.summary}</p>
          <div className="space-y-3">{(result.recommendations || []).map((r,i)=>(
            <div key={i} className="border-l-4 border-[#F97316] bg-[#FFF7ED] p-3 rounded">
              <div className="font-bold text-[#0A0F1C] text-sm">{r.focus}</div>
              <div className="text-xs text-[#475569] mt-1">{r.why}</div>
              <div className="text-xs text-[#1D4ED8] font-bold mt-2">→ {r.suggested_action}</div>
            </div>
          ))}</div>
        </>}
      </div>
    </div>
  );
}
